import DataLoader from 'dataloader';
import { match } from 'ts-pattern';
import {
  DataFields,
  DataTypes,
  EmployeeDataFields,
  Operations,
} from '../constants/constants';
import { EnvironmentService } from '../environment/environment-service';
import { ErrorStore } from '../error-store';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  GroupingInput,
  RegularMetricId,
  SqlQueryResponse,
  TimeSegment,
  TimeSelectionInput,
} from '../graphql/generated/graphql-sdk';
import { PermissionBasedPermissionsStore } from '../permissions/permissions-store';
import { Hasher } from '../v2/api/hasher';
import {
  ApiValueDataType,
  HasDependencies,
  hashString,
  Role,
} from '../v2/api/types';
import { handleGQLErrors, parseCustomSqlValue } from '../v2/api/utils';
import { ErrorTracker } from '../v2/common/components/errors/trackers/ErrorTracker';
import { Domains } from '../v2/constants';
import {
  ApiCustomSqlQueryResponseDataPoint,
  ApiCustomSqlQueryResponseDataPointDimensionData,
  ApiCustomSqlQueryResponseDataPointMeasureData,
  ApiMasterDataAdvancedQueryDimension,
  ApiMasterDataBatchQueryResponse,
  ApiMasterDataBatchQueryResponseItem,
  ApiMasterDataField,
  ApiMasterDataMovementQuery,
  ApiMasterDataQueryMeasure,
  ApiMasterDataQueryMeasureOperation,
  ApiMasterDataQueryResponse,
  ApiMasterDataTypes,
  ApiMetricQueryResponse,
  ApiQueryTypes,
} from './api-interfaces';
import { GraphQlRequestService } from './graphql-request-service';
import { MetricResultFailure, MetricResultSuccess } from './graphql-types';
import { RequestService } from './request-service';

export interface BenchmarkConfig {
  prduId?: string | null;
  pdu?: boolean;
  limitedToPermittedPopulation?: boolean;
}

type QueryInput<Q, R> = {
  query: Q;
  processResponse: (result: R) => ApiMasterDataQueryResponse;
};

type ApiCustomSqlQuery = { querySql: string; disableNestLoop?: boolean };

interface CustomSqlQueryInput
  extends QueryInput<ApiCustomSqlQuery, SqlQueryResponse> {
  queryType: ApiQueryTypes.SQL;
  query: ApiCustomSqlQuery;
  processResponse: (result: SqlQueryResponse) => ApiMasterDataQueryResponse;
}

interface MovementQueryInput
  extends QueryInput<ApiMasterDataMovementQuery, string | null> {
  queryType: ApiQueryTypes.MOVEMENT;
  query: ApiMasterDataMovementQuery;
  processResponse: (result: string | null) => ApiMasterDataQueryResponse;
}

type QueryInputType = CustomSqlQueryInput | MovementQueryInput;

export type SQLFilters = string;

export interface MasterDataApiService extends HasDependencies {
  executeCustomSqlQuery(
    domain: Domains,
    dimensions: ApiMasterDataAdvancedQueryDimension[],
    measures: ApiMasterDataQueryMeasure[],
    query: string,
    disableNestLoop?: boolean
  ): Promise<ApiMasterDataQueryResponse>;
  executeMovementQuery(
    domain: Domains,
    query: ApiMasterDataMovementQuery
  ): Promise<ApiMasterDataQueryResponse>;
  queryCohortMetrics(
    domain: Domains,
    timeSelection: TimeSelectionInput,
    metrics: CohortMetricId[],
    userCohortFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse>;
  queryEmployeeCohortMetrics(
    domain: Domains,
    timeSelection: TimeSelectionInput,
    metrics: EmployeeCohortMetricId[],
    userFilters?: SQLFilters | null,
    userCohortFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse>;
  queryMetrics(
    domain: Domains,
    timeSelection: TimeSelectionInput,
    metrics: RegularMetricId[],
    userFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse>;
}

// This service uses the new backend for old, advanced and movement queries and otherwise uses the old backend
// tslint:disable-next-line: max-classes-per-file
export class NewBackendMasterDataApiService implements MasterDataApiService {
  private isPanalytSuperAdminAndNonProdEnv: boolean;

  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly requestService: RequestService,
    readonly permissionsStore: PermissionBasedPermissionsStore,
    readonly environmentService: EnvironmentService,
    readonly executorRole: Role,
    readonly simulateRole: Role | null,
    readonly errorTracker: ErrorTracker,
    readonly errorStore: ErrorStore
  ) {
    this.isPanalytSuperAdminAndNonProdEnv =
      permissionsStore.canManageAllDomains && !environmentService.isProd;
  }

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      ...this.requestService.getDeps(),
      ...this.permissionsStore.getDeps(),
      ...this.environmentService.getDeps(),
      Hasher.hash(this.executorRole),
      Hasher.hash(this.simulateRole),
      ...this.errorTracker.getDeps(),
    ]),
  ];

  public async executeCustomSqlQuery(
    domain: Domains,
    dimensions: ApiMasterDataAdvancedQueryDimension[],
    measures: ApiMasterDataQueryMeasure[],
    queryString: string,
    disableNestLoop?: boolean
  ): Promise<ApiMasterDataQueryResponse> {
    const queryDataLoader = this.getQueryDataLoader(domain);
    return (
      await queryDataLoader.load({
        queryType: ApiQueryTypes.SQL,
        query: { querySql: queryString, disableNestLoop },
        processResponse: this.toSqlQueryResponse(dimensions, measures),
      })
    ).response;
  }

  private toSqlQueryResponse =
    (
      dimensions: ApiMasterDataAdvancedQueryDimension[],
      measures: ApiMasterDataQueryMeasure[]
    ) =>
    (result: SqlQueryResponse): ApiMasterDataQueryResponse => {
      const rows = result?.data;
      return {
        dataPoints:
          rows?.map((row) =>
            row.reduce(
              (acc, value, index) => {
                const parsedValue = parseCustomSqlValue(
                  value as ApiValueDataType
                );
                if (dimensions[index]) {
                  const dim = dimensions[index];
                  const dataType = dim.dataType;
                  const dataField = dim.property;
                  acc['dimensions'].push({
                    dataType: dataType as ApiMasterDataTypes,
                    property: dataField as ApiMasterDataField,
                    value: parsedValue,
                  });
                } else if (measures[index - dimensions.length]) {
                  const measure = measures[index - dimensions.length];
                  acc['measures'].push({
                    dataType: measure.dataType as ApiMasterDataTypes,
                    property: measure.property as ApiMasterDataField,
                    value: parsedValue,
                    operation: measure.operation,
                  });
                } else {
                  throw new Error('Missing dimension or measure');
                }
                return acc;
              },
              {
                dimensions: [],
                measures: [],
              } as ApiCustomSqlQueryResponseDataPoint
            )
          ) ?? [],
      };
    };

  private domainToDataLoaderMap: Record<
    string,
    DataLoader<QueryInputType, ApiMasterDataBatchQueryResponseItem>
  > = {};

  private getQueryDataLoader = (domain: Domains) => {
    if (this.domainToDataLoaderMap[domain]) {
      return this.domainToDataLoaderMap[domain];
    } else {
      const batchFn = async (keys: readonly QueryInputType[]) => {
        // This fails if any of the queries fail, returning 206
        // See this discussion https://panalyt.slack.com/archives/C020B3RUR9U/p1717389224240879
        return (await this.executeGraphqlQueriesInBatch(domain, [...keys]))
          .responseListItems;
      };
      const dataLoader = new DataLoader<
        QueryInputType,
        ApiMasterDataBatchQueryResponseItem
      >((keys) => batchFn(keys), {
        cache: false,
        // We should enable cache eventually and maybe replace our in memory cache with this
        // One benefit of using data loader cache is that it comes with deduplication
        // However, when we enable cache, we might need to provide a custom cache as the default one
        // uses reference equality check for object keys
        maxBatchSize: 100,
        // We tried out different batch sizes and 100 seems to be fine. Less is causing
        // error due to too many requests. Although we can still continue experimenting
        // and refine the batch size for further optimization. This might also depend on
        // backend and db configurations
      });
      this.domainToDataLoaderMap[domain] = dataLoader;
      return this.domainToDataLoaderMap[domain];
    }
  };

  public async executeMovementQuery(
    domain: Domains,
    query: ApiMasterDataMovementQuery
  ): Promise<ApiMasterDataQueryResponse> {
    const queryDataLoader = this.getQueryDataLoader(domain);
    return (
      await queryDataLoader.load({
        queryType: ApiQueryTypes.MOVEMENT,
        query,
        processResponse: (r: string | null) =>
          r
            ? (JSON.parse(r) as ApiMasterDataQueryResponse)
            : { dataPoints: [] },
      })
    ).response;
  }

  private customSqlQueryExecutorFieldSelection = `{ data {
    __typename
    ... on BigDecimalValue {
      bigDecimal: value
    }
    ... on BooleanValue {
      boolean: value
    }
    ... on DateValue {
      date: value
    }
    ... on DoubleValue {
      double: value
    }
    ... on IntValue {
      int: value
    }
    ... on StringValue {
      string: value
    }
    ... on TimestampValue {
      timestamp: value
    }
    ... on JsonValue {
      json: value
    }
    ... on Confidential {
      reason
    }
   } }`;

  private async executeGraphqlQueriesInBatch(
    domain: Domains,
    queries: QueryInputType[]
  ): Promise<ApiMasterDataBatchQueryResponse> {
    const graphQlClient = this.graphQlRequestService.graphQlClientBatching;
    if (!graphQlClient) {
      return Promise.reject('GraphQlClient is not initialized');
    }
    const selectedExecutorRole = `"${this.executorRole.id}"`;
    const simulateRoleId = this.simulateRole?.id;
    const simulateRole = simulateRoleId ? `"${simulateRoleId}"` : null;

    const graphQlQueries = queries.reduce<{
      [ApiQueryTypes.MOVEMENT]: Record<
        string,
        {
          queryString: string;
          processResponse: (result: string) => ApiMasterDataQueryResponse;
        }
      >;
      [ApiQueryTypes.SQL]: Record<
        string,
        {
          queryString: string;
          processResponse: (
            result: SqlQueryResponse
          ) => ApiMasterDataQueryResponse;
        }
      >;
    }>(
      (acc, q, i) => {
        const queryAlias = `query${i}`;
        return match(q)
          .with({ queryType: ApiQueryTypes.MOVEMENT }, (queryInput) => {
            const query = queryInput.query;
            const disableNestLoop = query.disableNestLoop ?? false;
            const graphQlQuery = `${ApiQueryTypes.MOVEMENT}(domain:"${domain}", queryJson:"""${JSON.stringify(
              query
            )}""", selectedExecutorRole: ${selectedExecutorRole}, simulateRole: ${simulateRole}, disableNestLoop: ${disableNestLoop})`;
            return {
              ...acc,
              [ApiQueryTypes.MOVEMENT]: {
                ...acc[ApiQueryTypes.MOVEMENT],
                [queryAlias]: {
                  queryString: graphQlQuery,
                  processResponse: queryInput.processResponse,
                },
              },
            };
          })
          .with({ queryType: ApiQueryTypes.SQL }, (queryInput) => {
            const query = queryInput.query;
            const querySql = query.querySql;
            const disableNestLoop = query.disableNestLoop ?? false;
            const graphQlQuery = `${ApiQueryTypes.SQL}(domain:"${domain}", querySql: """${querySql} """, selectedExecutorRole: ${selectedExecutorRole}, simulateRole: ${simulateRole}, disableNestLoop: ${disableNestLoop}) ${this.customSqlQueryExecutorFieldSelection}`;
            return {
              ...acc,
              [ApiQueryTypes.SQL]: {
                ...acc[ApiQueryTypes.SQL],
                [queryAlias]: {
                  queryString: graphQlQuery,
                  processResponse: queryInput.processResponse,
                },
              },
            };
          })
          .exhaustive();
      },
      { [ApiQueryTypes.MOVEMENT]: {}, [ApiQueryTypes.SQL]: {} }
    );

    const responses: ApiMasterDataBatchQueryResponseItem[] = [];
    if (Object.keys(graphQlQueries[ApiQueryTypes.MOVEMENT]).length > 0) {
      const graphQlQueriesWithAlias = Object.entries(
        graphQlQueries[ApiQueryTypes.MOVEMENT]
      );
      const queries = `query { ${graphQlQueriesWithAlias.reduce(
        (acc, [alias, q]) => acc + `${alias}:${q.queryString} `,
        ''
      )} }`;
      const movementQueryResults =
        await graphQlClient.request<Record<string, string>>(queries);
      responses.push(
        ...graphQlQueriesWithAlias.flatMap(([alias, query]) => {
          const response = movementQueryResults[alias];
          if (response) {
            const processedResult = query.processResponse(response);
            return { query: query.queryString, response: processedResult };
          } else {
            console.error(
              `Did not expect alias ${alias} in graphql response. There is mismatch in the sent queries and the response. Skipping this query: ${query}`
            );
            return [];
          }
        })
      );
    }
    if (Object.keys(graphQlQueries[ApiQueryTypes.SQL]).length > 0) {
      const graphQlQueriesWithAlias = Object.entries(
        graphQlQueries[ApiQueryTypes.SQL]
      );
      const queries = `query { ${graphQlQueriesWithAlias.reduce(
        (acc, [alias, q]) => acc + `${alias}:${q.queryString} `,
        ''
      )} }`;
      const sqlQueryResults =
        await graphQlClient.request<Record<string, SqlQueryResponse>>(queries);
      responses.push(
        ...graphQlQueriesWithAlias.flatMap(([alias, query]) => {
          const response = sqlQueryResults[alias];
          if (response) {
            const processedResult = query.processResponse(response);
            return [{ query: query.queryString, response: processedResult }];
          } else {
            console.error(
              `Did not expect alias ${alias} in graphql response. There is mismatch in the sent queries and the response. Skipping this query: ${query}`
            );
            return [];
          }
        })
      );
    }
    return { responseListItems: responses };
  }

  public async queryCohortMetrics(
    domain: Domains,
    timeSelection: TimeSelectionInput,
    metrics: CohortMetricId[],
    userCohortFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse> {
    return handleGQLErrors(
      this.graphQlRequestService.graphQlSdk
        .queryCohortMetrics({
          domain,
          metrics,
          disableNestLoop: disableNestLoop ?? null,
          timeSelection,
          userCohortFilters: userCohortFilters ?? null,
          grouping: grouping ?? null,
          selectedExecutorRole: this.executorRole.id,
          simulateRole: this.simulateRole?.id ?? null,
        })
        .then((result) => {
          const results = result.queryCohortMetrics?.results;

          const failures = results?.filter(
            (r) => r.__typename === 'MetricResultFailure'
          ) as MetricResultFailure[];
          const successes = results?.filter(
            (r) => r.__typename === 'MetricResultSuccess'
          ) as MetricResultSuccess[];

          return (
            successes?.reduce((acc, r) => {
              const dataPoints = r.segments.flatMap((s) => {
                const res = s.groupSegments.map((gs) => {
                  const dimensions: ApiCustomSqlQueryResponseDataPointDimensionData[] =
                    this.parseTimeSegment(s.timeSegment);
                  const measures: ApiCustomSqlQueryResponseDataPointMeasureData[] =
                    [];
                  measures.push({
                    dataType: DataTypes.EMPLOYEE, //TODO: fix
                    operation:
                      Operations.EQUAL as ApiMasterDataQueryMeasureOperation,
                    property: r.metricId as unknown as ApiMasterDataField, //TODO: fix
                    value: parseCustomSqlValue(gs.data),
                  });
                  dimensions.push(
                    ...gs.groupSegment.map(
                      (gss) =>
                        ({
                          property: gss.dataField as DataFields,
                          dataType: gss.dataType as DataTypes,
                          value: parseCustomSqlValue(gss.value),
                        }) as ApiCustomSqlQueryResponseDataPointDimensionData
                    )
                  );
                  return { dimensions, measures, meta: r.meta };
                });
                return res;
              });
              acc = {
                dataPoints: [...(acc?.dataPoints ?? []), ...(dataPoints ?? [])],
              };
              return acc;
            }, {} as ApiMetricQueryResponse) ?? ({} as ApiMetricQueryResponse)
          );
        })
    );
  }

  public async queryEmployeeCohortMetrics(
    domain: Domains,
    timeSelection: TimeSelectionInput,
    metrics: EmployeeCohortMetricId[],
    userFilters?: SQLFilters | null,
    userCohortFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse> {
    return handleGQLErrors(
      this.graphQlRequestService.graphQlSdk
        .queryEmployeeCohortMetrics({
          domain,
          metrics,
          disableNestLoop: disableNestLoop ?? null,
          grouping: grouping ?? null,
          timeSelection,
          userFilters: userFilters ?? null,
          userCohortFilters: userCohortFilters ?? null,
          selectedExecutorRole: this.executorRole.id,
          simulateRole: this.simulateRole?.id ?? null,
        })
        .then((result) => {
          const results = result.queryEmployeeCohortMetrics?.results;

          const failures = results?.filter(
            (r) => r.__typename === 'MetricResultFailure'
          ) as MetricResultFailure[];
          const successes = results?.filter(
            (r) => r.__typename === 'MetricResultSuccess'
          ) as MetricResultSuccess[];

          return (
            successes?.reduce((acc, r) => {
              const dataPoints = r.segments.flatMap((s) => {
                const res = s.groupSegments.map((gs) => {
                  const dimensions: ApiCustomSqlQueryResponseDataPointDimensionData[] =
                    this.parseTimeSegment(s.timeSegment);
                  const measures: ApiCustomSqlQueryResponseDataPointMeasureData[] =
                    [];
                  measures.push({
                    dataType: DataTypes.EMPLOYEE, //TODO: fix
                    operation:
                      Operations.EQUAL as ApiMasterDataQueryMeasureOperation,
                    property: r.metricId as unknown as ApiMasterDataField, //TODO: fix
                    value: parseCustomSqlValue(gs.data),
                  });
                  dimensions.push(
                    ...gs.groupSegment.map(
                      (gss) =>
                        ({
                          property: gss.dataField as DataFields,
                          dataType: gss.dataType as DataTypes,
                          value: parseCustomSqlValue(gss.value),
                        }) as ApiCustomSqlQueryResponseDataPointDimensionData
                    )
                  );
                  return { dimensions, measures, meta: r.meta };
                });
                return res;
              });
              acc = {
                dataPoints: [...(acc?.dataPoints ?? []), ...(dataPoints ?? [])],
              };
              return acc;
            }, {} as ApiMetricQueryResponse) ?? ({} as ApiMetricQueryResponse)
          );
        })
    );
  }

  public async queryMetrics(
    domain: Domains,
    timeSelection: TimeSelectionInput,
    metrics: RegularMetricId[],
    userFilters?: SQLFilters | null,
    grouping?: GroupingInput,
    disableNestLoop?: boolean
  ): Promise<ApiMetricQueryResponse> {
    return handleGQLErrors(
      this.graphQlRequestService.graphQlSdk
        .queryMetrics({
          domain,
          metrics,
          disableNestLoop: disableNestLoop ?? null,
          grouping: grouping ?? null,
          timeSelection,
          userFilters: userFilters ?? null,
          selectedExecutorRole: this.executorRole.id,
          simulateRole: this.simulateRole?.id ?? null,
        })
        .then((result) => {
          const results = result.queryMetrics?.results;

          const failures = results?.filter(
            (r) => r.__typename === 'MetricResultFailure'
          ) as MetricResultFailure[];
          const successes = results?.filter(
            (r) => r.__typename === 'MetricResultSuccess'
          ) as MetricResultSuccess[];

          return (
            successes?.reduce((acc, r) => {
              const dataPoints = r.segments.flatMap((s) => {
                const res = s.groupSegments.map((gs) => {
                  const dimensions: ApiCustomSqlQueryResponseDataPointDimensionData[] =
                    this.parseTimeSegment(s.timeSegment);
                  const measures: ApiCustomSqlQueryResponseDataPointMeasureData[] =
                    [];
                  measures.push({
                    dataType: DataTypes.EMPLOYEE, //TODO: fix
                    operation:
                      Operations.EQUAL as ApiMasterDataQueryMeasureOperation,
                    property: r.metricId as unknown as ApiMasterDataField, //TODO: fix
                    value: parseCustomSqlValue(gs.data),
                  });
                  dimensions.push(
                    ...gs.groupSegment.map(
                      (gss) =>
                        ({
                          property: gss.dataField as DataFields,
                          dataType: gss.dataType as DataTypes,
                          value: parseCustomSqlValue(gss.value),
                        }) as ApiCustomSqlQueryResponseDataPointDimensionData
                    )
                  );
                  return { dimensions, measures, meta: r.meta };
                });
                return res;
              });
              acc = {
                dataPoints: [...(acc?.dataPoints ?? []), ...(dataPoints ?? [])],
              };
              return acc;
            }, {} as ApiMetricQueryResponse) ?? ({} as ApiMetricQueryResponse)
          );
        })
    );
  }

  private parseTimeSegment(timeSegment: TimeSegment) {
    if (
      timeSegment.__typename === 'SingleValueTimeSegment' ||
      timeSegment.__typename === 'CalendarYearSingleValueByMonthsSegment'
    ) {
      return [];
    }
    const dimension: ApiCustomSqlQueryResponseDataPointDimensionData = {
      dataType: DataTypes.EMPLOYEE,
      property: EmployeeDataFields.VERSION_ID,
      value: match(timeSegment)
        .with(
          { __typename: 'CalendarYearYearlySegment' },
          (value) => `${value.year}`
        )
        .with(
          { __typename: 'CalendarYearQuarterlySegment' },
          (value) => `${value.quarter.year}-${value.quarter.quarterOfYear}`
        )
        .with(
          { __typename: 'CalendarYearMonthlySegment' },
          (value) => value.date
        )
        .with(
          { __typename: 'FinancialYearYearlySegment' },
          (value) => `${value.year}`
        )
        .with(
          { __typename: 'FinancialYearQuarterlySegment' },
          (value) => `${value.quarter.year}-${value.quarter.quarterOfYear}`
        )
        .otherwise(() => {
          throw new Error('time segment is invalid');
        }),
    };
    return [dimension];
  }
}
